import React, { useEffect, useRef, useState } from "react";
import { Container } from "@mui/material";
import {
  TipsAndUpdates,
  Draw,
  Handyman,
  Verified,
  AccessTime,
  Mail,
  Timer,
  Tune,
  WarningAmber,
} from "@mui/icons-material";
import InfoBox from "../../base/InfoBox";
import InfoBoxGroup from "../../base/InfoBoxGroup";
import { useDispatch, useSelector } from "../../../store";
import { fetchSettings, saveSettings } from "../../../slices/settings";
import Settings from "../../../types/settings";
import { useForm } from "react-hook-form";
import Modal from "../../base/Modal";
import EmailNotifications, {
  EmailFormSubmitHandler,
} from "./EmailNotificationsForm";
import NotificationSettings from "../../../types/notificationSettings";
import PushNotifications, {
  PushFormSubmitHandler,
} from "./PushNotificationsForm";
import SvNotifications, { SvFormSubmitHandler } from "./SvNotificationsForm";
import {
  addEmailGroup,
  deleteEmailGroups,
  editEmailGroup,
} from "../../../slices/emailGroup";

interface Props {}

const NotificationsView: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const { settings } = useSelector((store) => store.settings);
  const { emailGroups, status } = useSelector((store) => store.emails);

  const emailFormSubmitHandlerRef = useRef<EmailFormSubmitHandler | null>(null);
  const pushFormSubmitHandlerRef = useRef<PushFormSubmitHandler | null>(null);
  const svFormSubmitHandlerRef = useRef<SvFormSubmitHandler | null>(null);

  const [modalOpen, setModalOpen] = useState<"email" | "site" | "sv" | "none">(
    "none"
  );

  const { watch, reset, setValue } = useForm<Settings>({
    mode: "onChange",
    defaultValues: {
      ...settings,
    },
  });

  const formValues = watch();

  const handleSaveSettings = async (data: Settings) => {
    await dispatch(saveSettings(data));
  };

  const handleSaveEmailGroups = async (data: NotificationSettings) => {
    const addGroups = data.emailGroups;
    const deleteGroups = emailGroups?.filter(
      (x) => !addGroups?.map((g) => g.id)?.includes(x.id)
    );

    addGroups?.forEach(async (g) => {
      g.id! > 0
        ? await dispatch(editEmailGroup(g))
        : await dispatch(addEmailGroup(g));
    });
    deleteGroups?.forEach(
      async (g) => await dispatch(deleteEmailGroups(g.id!))
    );

    handleCloseModal();
  };

  const handleSavePushData = (data: NotificationSettings) => {
    handleCloseModal();
  };

  const handleSaveSvData = async (data: NotificationSettings) => {
    await handleSaveSettings({
      ...formValues,
      notificationSettings: {
        ...formValues.notificationSettings,
        ...data,
      },
    });

    handleCloseModal();
  };

  useEffect(() => {
    if (!!settings) {
      reset(settings);
    }
  }, [dispatch, reset, settings]);

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch]);

  const handleCloseModal = () => {
    setModalOpen("none");
  };

  const handleOpenEmail = () => {
    setModalOpen("email");
  };

  const handleOpenSv = () => {
    setModalOpen("sv");
  };

  const emailInfoBoxes = [
    {
      text: "Set up groups to sort users who will receive notifications via email or text message.",
      icon: <Mail fontSize="inherit" color="info" />,
    },
    {
      text: "Choose from emails and phone numbers of registered users or enter new email addresses.",
      icon: <Verified fontSize="inherit" color="success" />,
    },
    {
      text: "Add the group to a tile to send on a schedule or when certain conditions are met.",
      icon: <AccessTime fontSize="inherit" color="secondary" />,
    },
  ];

  const siteInfoBoxes = [
    {
      text: "Select types of alerts and set thresholds on individual tiles.",
      icon: <WarningAmber fontSize="inherit" color="error" />,
    },
    {
      text: "Allow users to configure their own push notifications.",
      icon: <Tune fontSize="inherit" color="primary" />,
    },
    {
      text: "Alerts appear in real time with a notification in the navbar.",
      icon: <Timer fontSize="inherit" color="warning" />,
    },
  ];

  const svInfoBoxes = [
    {
      text: "Configure integrations with Getting More Done, Responder, and Investigator.",
      icon: <TipsAndUpdates fontSize="inherit" color="inherit" />,
    },
    {
      text: "Enable options for other SVPro sites to create notification flows to other portals.",
      icon: <Draw fontSize="inherit" color="secondary" />,
    },
    {
      text: "When alert conditions are met, send information to other portals for review.",
      icon: <Handyman fontSize="inherit" color="success" />,
    },
  ];

  return (
    <Container sx={{ marginBottom: 8 }}>
      <form>
        <InfoBoxGroup
          header={"Notification Groups"}
          switchFunction={{
            state: formValues.notificationSettings?.emailNotifications ?? false,
            toggleSwitch: (e) => {
              setValue(
                "notificationSettings.emailNotifications",
                !formValues.notificationSettings?.emailNotifications
              );
              handleSaveSettings({
                ...formValues,
                notificationSettings: {
                  ...formValues.notificationSettings,
                  emailNotifications: e,
                },
              });
            },
          }}
          editFunction={handleOpenEmail}
          content={emailInfoBoxes.map((info, index) => {
            return <InfoBox key={index} text={info.text} icon={info.icon} />;
          })}
        />
        <InfoBoxGroup
          header={"Site Notifications"}
          switchFunction={{
            state:
              (formValues.notificationSettings?.siteNotifications ||
                formValues.notificationSettings?.textNotifications) ??
              false,
            toggleSwitch: (e) => {
              setValue(
                "notificationSettings.siteNotifications",
                !formValues.notificationSettings?.siteNotifications
              );
              handleSaveSettings({
                ...formValues,
                notificationSettings: {
                  ...formValues.notificationSettings,
                  siteNotifications: e,
                },
              });
            },
          }}
          // editFunction={handleOpenSite}
          content={siteInfoBoxes.map((info, index) => {
            return <InfoBox key={index} text={info.text} icon={info.icon} />;
          })}
        />
        <InfoBoxGroup
          header={"SVPro Notifications"}
          switchFunction={{
            state: formValues.notificationSettings?.svNotifications ?? false,
            toggleSwitch: (e) => {
              setValue(
                "notificationSettings.svNotifications",
                !formValues.notificationSettings?.svNotifications
              );
              handleSaveSettings({
                ...formValues,
                notificationSettings: {
                  ...formValues.notificationSettings,
                  svNotifications: e,
                },
              });
            },
          }}
          editFunction={handleOpenSv}
          content={svInfoBoxes.map((info, index) => {
            return <InfoBox key={index} text={info.text} icon={info.icon} />;
          })}
        />
      </form>
      <Modal
        open={modalOpen === "email"}
        onClose={handleCloseModal}
        onSave={() => emailFormSubmitHandlerRef.current?.()}
        title={`Set Up Notification Groups`}
        loading={status === "loading"}
      >
        <EmailNotifications
          onSave={handleSaveEmailGroups}
          ref={emailFormSubmitHandlerRef}
        />
      </Modal>
      <Modal
        open={modalOpen === "site"}
        onClose={handleCloseModal}
        onSave={() => pushFormSubmitHandlerRef.current?.()}
        title={`Set Up Push Notifications`}
        loading={status === "loading"}
      >
        <PushNotifications
          onSave={handleSavePushData}
          ref={pushFormSubmitHandlerRef}
        />
      </Modal>
      <Modal
        open={modalOpen === "sv"}
        onClose={handleCloseModal}
        onSave={() => svFormSubmitHandlerRef.current?.()}
        title={`Set Up SVPro Notifications`}
        loading={status === "loading"}
      >
        <SvNotifications
          onSave={handleSaveSvData}
          ref={svFormSubmitHandlerRef}
        />
      </Modal>
    </Container>
  );
};

export default NotificationsView;
